import {
  Box,
  Center,
  Heading,
  HStack,
  Icon,
  Image,
  Show,
  Space,
  Text,
  VStack,
} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {
  OnlineOfferAlternative,
  OnlineOfferAlternativesResponseBody,
} from '../../../api/types/onlineOfferAlternativesTypes';
import {Button} from '../../../components/Button/Button';
import {useFormatAlternativeTotal} from '../../../hooks/useFormatAlternativeTotal';
import {getDeltaVsReference, hasTotal} from '../../../utils/buildComparisonRows';
import {getAlternativeChoiceStatus} from '../../../utils/getAlternativeChoiceStatus';
import {getAlternativeLabel} from '../../../utils/getAlternativeLabel';
import {getAlternativeUnavailableReason} from '../../../utils/getAlternativeUnavailableReason';
import {translateWithFallback as t} from '../../../utils/translateWithFallback';

interface ComparisonColumnHeaderProps {
  alternative: OnlineOfferAlternative;
  reference: OnlineOfferAlternative;
  offerAlternatives: OnlineOfferAlternativesResponseBody;
  onSelect: (alternative: OnlineOfferAlternative) => void;
  /** Opens the alternative on the offer page; absent when there is no offer page to open. */
  onView?: (alternative: OnlineOfferAlternative) => void;
}

export function ComparisonColumnHeader(props: ComparisonColumnHeaderProps) {
  const formatCurrency = useFormatCurrency();
  const formatTotal = useFormatAlternativeTotal();
  const delta = getDeltaVsReference(props.alternative, props.reference);
  const coverImage = props.alternative.imageUrls[0];
  const choiceStatus = getAlternativeChoiceStatus(props.offerAlternatives, props.alternative);
  const unavailableReason = getAlternativeUnavailableReason(props.alternative);

  const isReference = props.alternative.id === props.reference.id && hasTotal(props.alternative);
  // No total (PRICE_MISSING) means no delta either.
  const deltaLabel = match({isReference, delta})
    .with({isReference: true}, () => t('alternatives.comparison.referencePrice', 'Reference price'))
    .with({delta: Pattern.nonNullable}, ({delta: {amount, currency}}) =>
      t('alternatives.comparison.delta', '{sign} {amount} vs reference', {
        sign: Number(amount) < 0 ? '−' : '+',
        amount: formatCurrency(Math.abs(Number(amount)), currency) ?? '',
      })
    )
    .otherwise(() => null);

  // Full cell height, actions pinned to the bottom: titles wrap to different line counts, but the
  // prices and buttons of all columns still line up.
  return (
    <VStack spacing={2} height="100%">
      <Box height={28} borderRadius="small" overflow="hidden" backgroundColor="fill.greyLight">
        {isNotNil(coverImage) ? (
          <Image src={coverImage} width="100%" height="100%" fit="cover" />
        ) : (
          <Center height="100%">
            <VStack align="center" spacing={1}>
              <Icon value="car" size={8} color="text.tertiary" />
              <Text size="xxSmall" color="tertiary">
                {props.alternative.kind === 'CONFIGURATION'
                  ? t('alternatives.noImage.configuration', 'Built to order · no photo yet')
                  : t('alternatives.noImage.stock', 'No photo available')}
              </Text>
            </VStack>
          </Center>
        )}
      </Box>
      <Text size="xSmall" alternative color="accent">
        {getAlternativeLabel(props.alternative)}
      </Text>
      <Heading size={5}>{props.alternative.title}</Heading>
      <Show when={isNotNil(props.onView)}>
        <HStack>
          <Button
            variant="link"
            size="small"
            title={t('alternatives.comparison.viewDetails', 'View details')}
            onClick={() => props.onView?.(props.alternative)}
            data-testid={`alternativeView-${props.alternative.letter}`}
          />
        </HStack>
      </Show>
      <Space fillAvailable />
      <Heading size={3}>{formatTotal(props.alternative)}</Heading>
      <Show when={isNotNil(deltaLabel)}>
        <Text
          size="xSmall"
          color={isNotNil(delta) && Number(delta.amount) < 0 ? 'successDark' : 'secondary'}
        >
          {deltaLabel}
        </Text>
      </Show>
      <Show when={isNotNil(unavailableReason)}>
        <Text size="xSmall" color="warningDark">
          {unavailableReason}
        </Text>
      </Show>
      {choiceStatus !== 'NONE' ? (
        <Button
          variant="secondary"
          leftIcon="good"
          title={
            choiceStatus === 'PREPARING'
              ? t('alternatives.choice.preparing', 'Your adviser is preparing this vehicle')
              : t('alternatives.choice.selectedByYou', 'Selected by you')
          }
          isDisabled
          data-testid={`alternativeSelected-${props.alternative.letter}`}
        />
      ) : (
        <Button
          variant="primary"
          title={t('alternatives.choice.select', 'Select this alternative')}
          isDisabled={!props.offerAlternatives.canChoose || !props.alternative.selectable}
          onClick={() => props.onSelect(props.alternative)}
          data-testid={`alternativeSelect-${props.alternative.letter}`}
        />
      )}
    </VStack>
  );
}
